import { ShieldAlert, LogOut, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/authStore';
import { logout } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { runPrivyLogout } from '@/lib/runtime';

const ADMIN_APP_URL = import.meta.env.VITE_ADMIN_APP_URL as string | undefined;

/**
 * Gate content for admin/superadmin accounts (see `useRail1Link`, which sets
 * `adminGateRole` when `/link-rail1` reports the signed-in account has no
 * participant profile). Quests is a participant-only app.
 *
 * Rendered by `AppLayout` in place of the real app shell (not layered on top
 * of it) — the Sidebar/Outlet/modals simply aren't mounted while gated, so
 * there's no live app underneath this card to reveal by hiding it.
 */
export function AdminGateOverlay() {
  const adminGateRole = useAuthStore((s) => s.adminGateRole);
  const reset = useAuthStore((s) => s.reset);

  if (!adminGateRole) return null;

  const onSwitchAccount = async (): Promise<void> => {
    await logout();
    await runPrivyLogout();
    reset();
    queryClient.clear();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-gate-title"
      className="bw-card bw-card-amber w-full max-w-md p-8 text-center shadow-modal"
    >
      <span className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-amber2-600 ring-1 ring-amber2-100">
        <ShieldAlert className="h-6 w-6" aria-hidden="true" strokeWidth={1.75} />
      </span>
      <span className="eyebrow eyebrow-amber mb-3">
        {adminGateRole === 'superadmin' ? 'SUPERADMIN ACCOUNT' : 'ADMIN ACCOUNT'}
      </span>
      <h1
        id="admin-gate-title"
        className="font-display text-2xl font-bold tracking-tightish text-ink-950"
      >
        This app is for participants.
      </h1>
      <p className="mt-2 text-sm text-slate2-600">
        You're signed in with an admin account, which doesn't have a Quests profile. Log out
        and sign back in with a different wallet or email to use Quests, or head to the admin
        dashboard.
      </p>
      <div className="mt-6 flex flex-col gap-2">
        {ADMIN_APP_URL && (
          <a href={ADMIN_APP_URL} className="btn-water w-full justify-center">
            Open admin dashboard
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        )}
        <Button
          variant="ghost"
          onClick={onSwitchAccount}
          leftIcon={<LogOut className="h-4 w-4" aria-hidden="true" />}
          className="w-full justify-center"
        >
          Log out
        </Button>
      </div>
    </div>
  );
}

export default AdminGateOverlay;
